/**
 * BarcodeScanner.jsx
 * Barcode scanner modal with real OpenFoodFacts lookup
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, RefreshCw, Scale, Plus, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Common product barcodes for demo/testing
const DEMO_BARCODES = [
  { barcode: '7610200000019', name: 'Caotina Trinkschokolade' },
  { barcode: '7610848437505', name: 'Rivella Rot' },
  { barcode: '7613034626844', name: 'Nescafé Gold' },
  { barcode: '4005500249917', name: 'Haribo Goldbären' },
  { barcode: '4017100024009', name: 'Ovomaltine' },
];

export const BarcodeScanner = ({ isOpen, onClose, onProductFound, onCreateProduct, portalToken, mealType }) => {
  const [barcode, setBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [notFoundBarcode, setNotFoundBarcode] = useState('');
  const [servingSize, setServingSize] = useState(100);
  const [includeQr, setIncludeQr] = useState(false);
  const [lowLightMode, setLowLightMode] = useState(true);
  const [scanHint, setScanHint] = useState('Strichcode horizontal halten');
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (loading) {
      setScanHint(lowLightMode ? 'Bei wenig Licht: ruhig halten und 15–20cm Abstand' : 'Code wird gesucht...');
      return;
    }

    if (includeQr) {
      setScanHint(lowLightMode ? 'QR/Strichcode bei wenig Licht mittig halten' : 'QR oder Strichcode mittig halten');
    } else {
      setScanHint(lowLightMode ? 'Strichcode horizontal, 15–20cm Abstand' : 'Strichcode horizontal halten');
    }
  }, [includeQr, loading, lowLightMode]);

  // Lookup barcode
  const lookupBarcode = async (code) => {
    if (!code || code.length < 8 || !portalToken) return;
    
    setLoading(true);
    setError(null);
    setProduct(null);
    setNotFoundBarcode('');
    
    try {
      const response = await axios.get(`${API}/portal/nutrition/barcode/${code}`, {
        headers: { Authorization: `Bearer ${portalToken}` },
        timeout: lowLightMode ? 7500 : 4500
      });
      setProduct(response.data);
    } catch (err) {
      console.error('Barcode lookup error:', err);
      if (err.response?.status === 404 || err.response?.status === 504) {
        setNotFoundBarcode(code);
        setError(
          err.response?.status === 404
            ? 'Produkt nicht gefunden. Du kannst es jetzt als neues Produkt erstellen.'
            : 'Produkt aktuell nicht verfügbar. Du kannst es jetzt als neues Produkt erstellen.'
        );
      } else {
        setError('Fehler beim Abrufen der Produktdaten');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate nutrition based on serving size
  const calculateNutrition = (nutrition, grams) => {
    if (!nutrition) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    const factor = grams / 100;
    return {
      calories: Math.round((nutrition.calories || 0) * factor),
      protein: Math.round((nutrition.protein || 0) * factor * 10) / 10,
      carbs: Math.round((nutrition.carbs || 0) * factor * 10) / 10,
      fat: Math.round((nutrition.fat || 0) * factor * 10) / 10,
    };
  };

  // Add product to meal
  const handleAddProduct = async () => {
    if (!product) return;
    
    const nutrition = calculateNutrition(product.nutrition_per_100g || product, servingSize);
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    try {
      await axios.post(
        `${API}/portal/nutrition/entries`,
        {
          date: today,
          meal_type: mealType || (() => {
            const hour = new Date().getHours();
            if (hour >= 5 && hour < 10) return 'breakfast';
            if (hour >= 10 && hour < 12) return 'snack_morning';
            if (hour >= 12 && hour < 14) return 'lunch';
            if (hour >= 14 && hour < 17) return 'snack_afternoon';
            if (hour >= 17 && hour < 21) return 'dinner';
            return 'snack_evening';
          })(),
          food_name: product.name,
          food_id: product.id || product.barcode,
          brand: product.brand,
          barcode: product.barcode,
          serving_size: servingSize,
          calories: nutrition.calories,
          protein: nutrition.protein,
          carbs: nutrition.carbs,
          fat: nutrition.fat,
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success(`${product.name} hinzugefügt`);
      onProductFound?.(product, servingSize);
      setProduct(null);
      setBarcode('');
      onClose();
    } catch (err) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  // Handle barcode input
  const handleBarcodeChange = (e) => {
    const value = includeQr ? e.target.value.trim() : e.target.value.replace(/\D/g, '');
    setBarcode(value);
    setError(null);
    setProduct(null);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (includeQr && /[^0-9]/.test(value)) {
      setError('QR-Live-Scan ist nur im echten Scanner verfügbar. In der Preview bitte Barcode-Ziffern eingeben.');
      return;
    }

    const minLength = includeQr ? 6 : 8;
    if (value.length >= minLength) {
      debounceRef.current = setTimeout(() => {
        lookupBarcode(value);
      }, 220);
    }
  };

  if (!isOpen) return null;

  const nutrition = product?.nutrition_per_100g || product;

  return (
    <div className="absolute inset-0 bg-black/95 z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 bg-zinc-800 rounded-lg">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
          <h2 className="text-white font-bold flex-1">Barcode Scanner</h2>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Scanner Simulation */}
        <div className="bg-zinc-800/70 rounded-2xl p-6 mb-4 border border-dashed border-zinc-600">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-yellow-500/20 rounded-xl flex items-center justify-center mb-4">
              <Camera className="w-8 h-8 text-yellow-400" />
            </div>
            <p className="text-white font-medium mb-2">Barcode eingeben</p>
            <p className="text-zinc-500 text-sm text-center mb-4">
              Mobile Preview: Manuelle Eingabe für Barcode-Lookup
            </p>

            <div className="flex flex-wrap gap-2 justify-center mb-3">
              <button
                type="button"
                onClick={() => setIncludeQr((prev) => !prev)}
                className={`px-3 py-1 rounded-full text-xs border ${includeQr ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300' : 'bg-zinc-700 border-zinc-600 text-zinc-200'}`}
                data-testid="mobile-preview-scan-mode-toggle"
              >
                {includeQr ? 'QR + Strichcode' : 'Nur Strichcode'}
              </button>
              <button
                type="button"
                onClick={() => setLowLightMode((prev) => !prev)}
                className={`px-3 py-1 rounded-full text-xs border ${lowLightMode ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300' : 'bg-zinc-700 border-zinc-600 text-zinc-200'}`}
                data-testid="mobile-preview-low-light-toggle"
              >
                {lowLightMode ? 'Low-Light AN' : 'Low-Light AUS'}
              </button>
            </div>

            <p className="text-[11px] text-zinc-400 mb-3" data-testid="mobile-preview-scan-hint">
              {scanHint}
            </p>
            
            <input
              ref={inputRef}
              type="text"
              value={barcode}
              onChange={handleBarcodeChange}
              placeholder={includeQr ? 'z.B. QR-Text oder Barcode' : 'z.B. 7610200000019'}
              className="w-full px-4 py-3 bg-zinc-700 rounded-xl text-white text-center text-lg tracking-widest placeholder-zinc-500"
              maxLength={includeQr ? 64 : 13}
              data-testid="mobile-barcode-input"
            />
          </div>
        </div>

        {/* Demo Barcodes */}
        <div className="mb-4">
          <p className="text-zinc-500 text-xs mb-2">Beispiel-Barcodes zum Testen:</p>
          <div className="flex flex-wrap gap-2">
            {DEMO_BARCODES.map((demo) => (
              <button
                key={demo.barcode}
                onClick={() => {
                  setBarcode(demo.barcode);
                  lookupBarcode(demo.barcode);
                }}
                className="px-3 py-1.5 bg-zinc-800 rounded-lg text-xs text-zinc-400 hover:bg-zinc-700"
                data-testid={`mobile-barcode-demo-${demo.barcode}`}
              >
                {demo.name}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-yellow-400" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4" data-testid="mobile-barcode-error-state">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>

            {notFoundBarcode && onCreateProduct && (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => onCreateProduct(notFoundBarcode)}
                  className="flex-1 py-2.5 px-3 bg-yellow-500 text-black rounded-lg text-sm font-semibold"
                  data-testid="mobile-barcode-create-product-button"
                >
                  Neues Produkt erstellen
                </button>
                <button
                  onClick={() => {
                    setError(null);
                    setNotFoundBarcode('');
                    inputRef.current?.focus();
                  }}
                  className="flex-1 py-2.5 px-3 bg-zinc-700 text-zinc-200 rounded-lg text-sm"
                  data-testid="mobile-barcode-retry-button"
                >
                  Erneut scannen
                </button>
              </div>
            )}
          </div>
        )}

        {/* Product Found */}
        {product && !loading && (
          <div className="bg-zinc-800/70 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-4">
              {product.image_url ? (
                <img 
                  src={product.image_url} 
                  alt={product.name}
                  className="w-16 h-16 rounded-lg object-cover bg-zinc-700"
                />
              ) : (
                <div className="w-16 h-16 bg-zinc-700 rounded-lg flex items-center justify-center">
                  <Scale className="w-8 h-8 text-zinc-500" />
                </div>
              )}
              <div className="flex-1">
                <p className="text-white font-medium">{product.name}</p>
                <p className="text-zinc-500 text-sm">{product.brand}</p>
              </div>
            </div>

            {/* Nutrition per 100g */}
            <div className="grid grid-cols-4 gap-2 mb-4 p-3 bg-zinc-900/50 rounded-lg">
              {[
                { label: 'Kalorien', value: `${Math.round(nutrition?.calories || 0)}`, unit: 'kcal', color: 'text-yellow-400' },
                { label: 'Protein', value: nutrition?.protein || 0, unit: 'g', color: 'text-blue-400' },
                { label: 'Kohlenhy.', value: nutrition?.carbs || 0, unit: 'g', color: 'text-orange-400' },
                { label: 'Fett', value: nutrition?.fat || 0, unit: 'g', color: 'text-purple-400' },
              ].map(m => (
                <div key={m.label} className="text-center">
                  <p className={`text-base font-bold ${m.color}`}>{m.value}{m.unit !== 'kcal' ? m.unit : ''}</p>
                  <p className="text-zinc-500 text-[10px]">{m.label}</p>
                </div>
              ))}
            </div>

            {/* Serving Size */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-zinc-400 text-sm">Portionsgrösse:</span>
              <div className="flex-1 flex items-center gap-2">
                {[50, 100, 150, 200].map(size => (
                  <button
                    key={size}
                    onClick={() => setServingSize(size)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      servingSize === size 
                        ? 'bg-yellow-500 text-black' 
                        : 'bg-zinc-700 text-zinc-300'
                    }`}
                  >
                    {size}g
                  </button>
                ))}
              </div>
            </div>

            {/* Calculated nutrition */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
              <p className="text-yellow-400 text-sm text-center">
                {servingSize}g = <span className="font-bold">{Math.round(calculateNutrition(nutrition, servingSize).calories || 0)} kcal</span>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Add Button */}
      {product && !loading && (
        <div className="p-4 border-t border-zinc-700">
          <button
            onClick={handleAddProduct}
            className="w-full py-3 bg-yellow-500 rounded-xl text-black font-bold flex items-center justify-center gap-2"
            data-testid="mobile-barcode-add-product-button"
          >
            <Plus className="w-5 h-5" />
            Hinzufügen
          </button>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanner;
